import React, { useCallback, useMemo, useState, useRef } from 'react';
import { ExternalDashboard } from '../../../Domain/Cloudwatch/ExternalDashboard';
import { generateWidgetConfig } from './metricConstants';
import { DeploymentCharts } from './Details/DeploymentCharts';
import { SpaceBetween } from '@amzn/awsui-components-react/polaris';
import { DeploymentDetailsTable } from './Details/DeploymentDetailsTable';

interface MetricsVisualizationProps {
  releaseVersion: string;
  stackName: string;
  isSDPDSMetric: boolean;
  domainIdentifier?: string;
}

const ONE_WEEK = 7* 24 * 60 * 60 * 1000;
const PERIOD_12_HOURS = 43200;

export const MetricsVisualization: React.FC<MetricsVisualizationProps> = React.memo(({releaseVersion, stackName, isSDPDSMetric, domainIdentifier}) => {
  const [metricsData, setMetricsData] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const tableRef = useRef(null);

  const timeConfig = useMemo(() => {
    const now = new Date();
    const endTime = new Date();
    const startTime = new Date(now.getTime() - ONE_WEEK);
    const utcOffset = new Date().getTimezoneOffset() * -1;

    return {
      period: PERIOD_12_HOURS,
      startTime: startTime,
      endTime: endTime,
      utcOffset
    };
  }, []);

  const widgets = useMemo(() =>
      generateWidgetConfig(releaseVersion, stackName, PERIOD_12_HOURS),
    [releaseVersion, stackName]
  );

  const handleDataReceived = useCallback((data: any) => {
    setMetricsData(data);
  }, []);

  const scrollToTable = () => {
    if (tableRef.current) {
      const scrollOffset = -20;
      const tableElement = tableRef.current;
      //calculates where to scroll on the page when a user clicks to view details. It finds the table's position and adds a small offset
      const finalScrollPosition = tableElement.getBoundingClientRect().top + window.pageYOffset + scrollOffset;
      window.scrollTo({ top: finalScrollPosition, behavior: 'smooth' });
    }
  };

  React.useEffect(() => {
    if (selectedStatus) {
      setTimeout(scrollToTable, 100);
    }
  }, [selectedStatus]);

  return (
    <SpaceBetween size={'l'}>
      {metricsData && (
        <DeploymentCharts
          stackName={stackName}
          releaseVersion={releaseVersion}
          metricsData={metricsData}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
        />
      )}
      <ExternalDashboard
        widgets={widgets}
        metricRange={timeConfig}
        utcOffset={timeConfig.utcOffset}
        onDataReceived={handleDataReceived}
        isSDPDSMetric={isSDPDSMetric}
        {...(!isSDPDSMetric && { domainIdentifier: domainIdentifier })}
      />
      {selectedStatus && (
        <div ref={tableRef}>
          <DeploymentDetailsTable
            stackName={stackName}
            releaseVersion={releaseVersion}
            status={selectedStatus}
            onClose={() => setSelectedStatus(null)}
          />
        </div>
      )}
    </SpaceBetween>
  );
});